import linkifyHtml from 'linkify-html';
import { PartialDeep } from 'type-fest';

import {
  ComponentTypes,
  invisibleComponentTypes,
  WidgetComponent,
} from '../types';
import { defaultStepContext } from './constants';
import { StepContextInterface } from './types';

const componentTypesThatHaveMessageBubbles: ComponentTypes[] = [
  'buttons',
  'restart_conversation',
  'text_message',
  'user_message',
  'visible_only_to_admins',
  'hyperlink_redirect',
  'form',
  'dynamic_card',
  'article_suggestion',
  'attachment_analysis_upload',
  'attachment_upload',
  'zendesk_attachment_upload',
] as const;

export type MessageBubblePosition =
  | 'lone'
  | 'first-in-a-batch'
  | 'middle'
  | 'last-in-a-batch';

function hasMessageBubble(
  widgetComponent: PartialDeep<WidgetComponent> | undefined,
) {
  return (
    widgetComponent?.component_type &&
    componentTypesThatHaveMessageBubbles.includes(
      widgetComponent.component_type,
    ) &&
    !(
      widgetComponent?.component_type === 'text_message' &&
      widgetComponent.component_fields &&
      'should_render_as_markdown' in widgetComponent.component_fields &&
      widgetComponent.component_fields.should_render_as_markdown &&
      'knowledge_articles' in widgetComponent.component_fields &&
      widgetComponent.component_fields.knowledge_articles
    ) &&
    !(
      widgetComponent?.component_type === 'article_suggestion' &&
      widgetComponent.component_fields &&
      'text_message_override' in widgetComponent.component_fields &&
      !widgetComponent.component_fields.text_message_override
    )
  );
}

const handleUserMessageBubblePosition = ({
  nextVisibleWidgetComponent,
  previousVisibleWidgetComponent,
  widgetComponent,
}: {
  nextVisibleWidgetComponent: PartialDeep<WidgetComponent> | undefined;
  previousVisibleWidgetComponent: PartialDeep<WidgetComponent> | undefined;
  widgetComponent: PartialDeep<WidgetComponent> & { index: number };
}) => {
  if (previousVisibleWidgetComponent?.component_type !== 'user_message') {
    return [
      widgetComponent.index,
      {
        user:
          nextVisibleWidgetComponent?.component_type === 'user_message'
            ? 'first-in-a-batch'
            : 'lone',
      },
    ] as const;
  }

  if (previousVisibleWidgetComponent?.component_type === 'user_message') {
    return [
      widgetComponent.index,
      {
        user:
          nextVisibleWidgetComponent?.component_type !== 'user_message'
            ? 'last-in-a-batch'
            : 'middle',
      },
    ] as const;
  }

  return [widgetComponent.index, { user: 'last-in-a-batch' }] as const;
};

const handleAgentConversationBubblePosition = ({
  nextVisibleWidgetComponent,
  previousVisibleWidgetComponent,
  widgetComponent,
}: {
  nextVisibleWidgetComponent: PartialDeep<WidgetComponent> | undefined;
  previousVisibleWidgetComponent: PartialDeep<WidgetComponent> | undefined;
  widgetComponent: PartialDeep<WidgetComponent> & { index: number };
}):
  | [number, { bot?: MessageBubblePosition; user?: MessageBubblePosition }]
  | null => {
  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    previousVisibleWidgetComponent?.component_type === 'user_message' &&
    nextVisibleWidgetComponent?.component_type === 'user_message'
  ) {
    return [widgetComponent.index, { bot: 'lone' }];
  }

  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    widgetComponent.component_type === 'text_message' &&
    widgetComponent.is_sent &&
    previousVisibleWidgetComponent &&
    'isInAgentConvo' in previousVisibleWidgetComponent &&
    previousVisibleWidgetComponent.isInAgentConvo &&
    hasMessageBubble(previousVisibleWidgetComponent) &&
    previousVisibleWidgetComponent?.is_sent &&
    previousVisibleWidgetComponent.component_type === 'text_message' &&
    nextVisibleWidgetComponent &&
    'isInAgentConvo' in nextVisibleWidgetComponent &&
    nextVisibleWidgetComponent.isInAgentConvo &&
    hasMessageBubble(nextVisibleWidgetComponent) &&
    nextVisibleWidgetComponent?.is_sent &&
    nextVisibleWidgetComponent.component_type === 'text_message'
  ) {
    return [
      widgetComponent.index,
      {
        bot: 'middle',
        ...(widgetComponent.is_sent ? { user: 'lone' } : {}),
      },
    ] as const;
  }

  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    widgetComponent.component_type === 'buttons' &&
    hasMessageBubble(previousVisibleWidgetComponent) &&
    nextVisibleWidgetComponent
  ) {
    return [
      widgetComponent.index,
      {
        bot: widgetComponent.is_sent ? 'last-in-a-batch' : 'middle',
        ...(widgetComponent.is_sent ? { user: 'lone' } : {}),
      },
    ] as const;
  }

  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    !nextVisibleWidgetComponent?.is_sent &&
    hasMessageBubble(previousVisibleWidgetComponent) &&
    nextVisibleWidgetComponent
  ) {
    return [widgetComponent.index, { bot: 'middle' }] as const;
  }

  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    widgetComponent.is_sent &&
    widgetComponent.component_type === 'text_message' && // ????????
    (previousVisibleWidgetComponent?.is_sent ||
      !hasMessageBubble(previousVisibleWidgetComponent)) &&
    previousVisibleWidgetComponent?.component_type !== 'text_message' &&
    hasMessageBubble(nextVisibleWidgetComponent)
  ) {
    return [
      widgetComponent.index,
      { bot: 'first-in-a-batch', user: 'lone' },
    ] as const;
  }

  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    widgetComponent.is_sent &&
    !hasMessageBubble(previousVisibleWidgetComponent) &&
    !hasMessageBubble(nextVisibleWidgetComponent)
  ) {
    return [
      widgetComponent.index,
      {
        bot: 'lone',
        user: 'lone',
      },
    ] as const;
  }

  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    widgetComponent.is_sent
  ) {
    return [
      widgetComponent.index,
      {
        bot:
          previousVisibleWidgetComponent?.component_type === 'user_message'
            ? 'lone'
            : ('last-in-a-batch' as const),
        user: 'lone',
      },
    ] as const;
  }

  if (
    'isInAgentConvo' in widgetComponent &&
    widgetComponent.isInAgentConvo &&
    widgetComponent.component_type === 'buttons' &&
    hasMessageBubble(previousVisibleWidgetComponent) &&
    nextVisibleWidgetComponent === undefined
  ) {
    return [
      widgetComponent.index,
      { bot: 'last-in-a-batch', user: 'lone' },
    ] as const;
  }

  return null;
};

export function getMessageBubblePositions(
  widgetComponents: PartialDeep<WidgetComponent>[],
) {
  return Object.fromEntries<{
    bot?: MessageBubblePosition;
    user?: MessageBubblePosition;
  } | null>(
    widgetComponents
      .map((widgetComponent, index) => ({
        ...widgetComponent,
        index,
      }))
      .filter(
        widgetComponent =>
          widgetComponent.component_type &&
          !invisibleComponentTypes.includes(widgetComponent.component_type),
      )
      .map((widgetComponent, index, visibleWidgetComponents) => {
        const nextVisibleWidgetComponent = visibleWidgetComponents[index + 1];
        const previousVisibleWidgetComponent =
          visibleWidgetComponents[index - 1];
        const isFirst = widgetComponent.index === 0;
        const isOnlyOneComponent = visibleWidgetComponents.length === 1;

        if (isFirst && isOnlyOneComponent) {
          return [
            widgetComponent.index,
            {
              bot: 'lone',
              ...(widgetComponent.is_sent ? { user: 'lone' } : {}),
            },
          ];
        }

        if (!hasMessageBubble(widgetComponent)) {
          return [widgetComponent.index, null];
        }

        if (widgetComponent.component_type === 'user_message') {
          return handleUserMessageBubblePosition({
            nextVisibleWidgetComponent,
            previousVisibleWidgetComponent,
            widgetComponent,
          });
        }

        const handleAgentConvoBubblePosition =
          handleAgentConversationBubblePosition({
            nextVisibleWidgetComponent,
            previousVisibleWidgetComponent,
            widgetComponent,
          });

        if (handleAgentConvoBubblePosition) {
          return handleAgentConvoBubblePosition;
        }

        const isInMiddle =
          (!widgetComponent.is_sent ||
            widgetComponent.component_type === 'hyperlink_redirect') &&
          !previousVisibleWidgetComponent?.is_sent &&
          nextVisibleWidgetComponent &&
          hasMessageBubble(previousVisibleWidgetComponent) &&
          hasMessageBubble(nextVisibleWidgetComponent) &&
          // if user message is following, it's not in the middle its at the end
          nextVisibleWidgetComponent.component_type !== 'user_message';

        // if a form or button step is sent
        const is_sentAndFollowingBotMessage =
          widgetComponent.is_sent &&
          previousVisibleWidgetComponent &&
          !previousVisibleWidgetComponent.is_sent &&
          hasMessageBubble(previousVisibleWidgetComponent);

        const isPreviousBubbleBotMessage =
          (!previousVisibleWidgetComponent?.is_sent ||
            previousVisibleWidgetComponent?.component_type ===
              'hyperlink_redirect') &&
          hasMessageBubble(previousVisibleWidgetComponent);

        const isNextInBatchAndNotUserMessage =
          !widgetComponent.is_sent &&
          nextVisibleWidgetComponent &&
          hasMessageBubble(nextVisibleWidgetComponent) &&
          nextVisibleWidgetComponent.component_type !== 'user_message';

        if (isInMiddle) {
          return [widgetComponent.index, { bot: 'middle' }];
        }

        if (is_sentAndFollowingBotMessage) {
          return [
            widgetComponent.index,
            { bot: 'last-in-a-batch', user: 'lone' },
          ];
        }

        if (isPreviousBubbleBotMessage) {
          return [
            widgetComponent.index,
            {
              bot: 'last-in-a-batch',
              ...(widgetComponent.is_sent ? { user: 'lone' } : {}),
            },
          ];
        }

        if (isNextInBatchAndNotUserMessage) {
          return [widgetComponent.index, { bot: 'first-in-a-batch' }];
        }

        return [
          widgetComponent.index,
          { bot: 'lone', ...(widgetComponent.is_sent ? { user: 'lone' } : {}) },
        ];
      }),
  );
}

export const getOverridableConfigValue = ({
  customConfigValue,
  defaultValue = '',
  widgetConfigValue,
}: {
  customConfigValue?: string;
  defaultValue?: string;
  widgetConfigValue?: string | null;
}): string => {
  if (customConfigValue) {
    return String(customConfigValue);
  }

  if (widgetConfigValue) {
    return widgetConfigValue;
  }

  return defaultValue;
};

const ignorePatterns = [/\d+\.Select$/, /\w+\.properties$/];

export function safeLinkifyHtml(html: string): string {
  try {
    return linkifyHtml(html, {
      rel: 'noreferrer',
      target: '_blank',
      truncate: 42,
      validate: {
        url: value =>
          !ignorePatterns.some(ignorePattern => ignorePattern.test(value)),
      },
    });
  } catch {
    return html;
  }
}

export const initializeStepContext = (
  customContext: Partial<StepContextInterface>,
) => {
  return { ...defaultStepContext, ...customContext };
};
